import { FC, Fragment } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { makeStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import Bronze from "../images/achievement-emblems/bronze.png";
import Silver from "../images/achievement-emblems/silver.png";
import Gold from "../images/achievement-emblems/gold.png";
import Platinum from "../images/achievement-emblems/platinum.png";
import Diamond from "../images/achievement-emblems/diamond.png";
import Master from "../images/achievement-emblems/master.png";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        achievementEmblem: {
            height: 140,
            paddingBottom: 10,
            width: 'inherit',
            textAlign: 'center',
            verticalAlign: 'middle',
            display: 'flex',
        },
        achievementEmblemImage: {
            height: 'auto',
            width: 120,
            margin: 'auto',
        },
        achievementRankName: {
            fontFamily: "Montserrat, Verdana, Sans-Serif",
            color: '#555',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '1rem',
            textTransform: 'uppercase',
            paddingBottom: 5,
        },
        achievementRankLabel: {
            fontFamily: "Montserrat, Verdana, Sans-Serif",
            color: '#555',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '0.7rem',
            lineHeight: '0.8rem',
            textTransform: 'uppercase',
            paddingTop: 4,
            marginBottom: 'none',
        },
        achievementRankValue: {
            fontFamily: "Montserrat, Verdana, Sans-Serif",
            color: '#555',
            textAlign: 'center',
            fontWeight: 300,
            fontSize: '1.1rem',
            lineHeight: '1.2rem',
            marginTop: 'none',
            paddingTop: 'none',
            paddingBottom: 4,
        },
        achievementDivider: {
            marginTop: '5px',
            marginBottom: '5px',
        },
        noAchievementEmblem: {
            height: 140,
            paddingBottom: 10,
            width: 'inherit',
            textAlign: 'center',
            verticalAlign: 'middle',
            display: 'flex',
        },
        noAchievementEmblemImage: {
            height: 'auto',
            width: 120,
            filter: 'grayscale(100%) contrast(50%) brightness(30%)',
            opacity: 0.3,
            margin: 'auto',
        },
        noAchievementText: {
            fontFamily: "Montserrat, Verdana, Sans-Serif",
            color: "#555",
            textAlign: 'center',
            fontWeight: 500,
            fontSize: '0.8rem',
            textTransform: 'uppercase',
        },
    })
);

interface AchievementInfoPanelProps {
    achievementRank: number;
    achievementPoints: number;
}

interface AchievementDetails {
    AchievementName: string;
    AchievementPointsNextTier: number;
    AchievementImage: string;
}

const determineAchievementDetails = (achievementPoints: number, achievementRank: number): AchievementDetails => {
    let details: AchievementDetails = {
        AchievementName: "",
        AchievementPointsNextTier: 0,
        AchievementImage: Diamond,
    }

    if (achievementPoints >= 10 && achievementPoints <= 4999) {
        details = {
            AchievementName: "Bronze",
            AchievementPointsNextTier: 5000 - achievementPoints,
            AchievementImage: Bronze,
        }
    } else if (achievementPoints >= 5000 && achievementPoints <= 19999) {
        details = {
            AchievementName: "Silver",
            AchievementPointsNextTier: 20000 - achievementPoints,
            AchievementImage: Silver,
        }
    } else if (achievementPoints >= 20000 && achievementPoints <= 29999) {
        details = {
            AchievementName: "Gold",
            AchievementPointsNextTier: 30000 - achievementPoints,
            AchievementImage: Gold,
        }
    } else if (achievementPoints >= 30000 && achievementPoints <= 35999) {
        details = {
            AchievementName: "Platinum",
            AchievementPointsNextTier: 36000 - achievementPoints,
            AchievementImage: Platinum,
        }
    } else if (achievementPoints >= 36000) {
        if (achievementRank <= 100) {
            details = {
                AchievementName: "Master",
                AchievementPointsNextTier: 0,
                AchievementImage: Master,
            }
        } else {
            details = {
                AchievementName: "Diamond",
                AchievementPointsNextTier: 0,
                AchievementImage: Diamond,
            }
        }
    }

    return details;
}

const AchievementInfoPanel: FC<AchievementInfoPanelProps> = (props: AchievementInfoPanelProps) => {
    const classes = useStyles();

    if (props.achievementRank === 0) {
        return (
            <Fragment>
                <div className={classes.noAchievementEmblem}>
                    <img className={classes.noAchievementEmblemImage} src={Diamond} alt="Achievement emblem placeholder" />
                </div>
                <Typography variant="subtitle1" className={classes.noAchievementText}>No achievement information available</Typography>
            </Fragment>
        );
    }

    const achievementDetails = determineAchievementDetails(props.achievementPoints, props.achievementRank);
    return (
        <Fragment>
            <div className={classes.achievementEmblem}>
                <img className={classes.achievementEmblemImage} alt={`${achievementDetails.AchievementName} emblem`} src={achievementDetails.AchievementImage} />
            </div>

            <Typography variant="subtitle1" className={classes.achievementRankName}>{achievementDetails.AchievementName}</Typography>
            <Divider className={classes.achievementDivider} />
            <Typography variant="subtitle1" component="p" className={classes.achievementRankLabel}>Achievement Rank</Typography>
            <Typography variant="subtitle1" component="p" className={classes.achievementRankValue}>{props.achievementRank.toLocaleString("en-US", { useGrouping: true })}</Typography>
            <Typography variant="subtitle1" component="p" className={classes.achievementRankLabel}>Achievement Points</Typography>
            <Typography variant="subtitle1" component="p" className={classes.achievementRankValue}>{props.achievementPoints.toLocaleString("en-US", { useGrouping: true })}</Typography>
            <Typography variant="subtitle1" component="p" className={classes.achievementRankLabel}>Points to Next Tier</Typography>
            <Typography variant="subtitle1" component="p" className={classes.achievementRankValue}>{achievementDetails.AchievementPointsNextTier.toLocaleString("en-US", { useGrouping: true })}</Typography>
        </Fragment>
    )
}

export default AchievementInfoPanel;
